import { isFunction } from '@mbrtargeting/metatag-utils';
import type { ObjectWithTcfApiCall, ObjectWithTcfApiReturn } from './interfaces.js';
import { extractData } from './utils/extract-data.js';
import { lookupCmpFrame } from './utils/frame-helper.js';

export interface CmpProxyOptions {
  win?: Window;
}

export const createCmpProxy = ({ win = window }: CmpProxyOptions = {}) => {
  // search for cmp frame to post messages to
  const cmpFrame = lookupCmpFrame(win);

  // map of calls
  const cmpCallbacks = new Map<string, (returnValue: unknown, success: boolean) => void>();

  /**
   * __tcfapi proxy method to do the postMessage and map the callback.
   *
   * From the caller's perspective, this function behaves identically to the CMP API's __tcfapi call
   */
  const tcfapiProxyHandler = (command: string, version: number, callback: (...args: any) => void, parameter?: any) => {
    const callId: string = `${Math.floor(Math.random() * 1e9)}`;

    const msg: ObjectWithTcfApiCall = {
      __tcfapiCall: { command, parameter, version, callId },
    };

    // map the callback for lookup on response
    cmpCallbacks.set(callId, callback);
    // send the message and hope for an answer
    cmpFrame?.postMessage(msg, '*');
  }

  /**
   * This postMessage handler is installed in the same window as the __tcfapi proxy method.
   * It will listen for __tcfapiReturn messages from the cmp iframe and call the mapped callback.
   */
  const postMessageProxyHandler = (event: MessageEvent): void => {
    const payload = extractData<ObjectWithTcfApiReturn>(event.data)?.__tcfapiReturn;

    if (payload) {
      // messages we care about will have a payload with a callId
      const cmpCallback = cmpCallbacks.get(payload.callId);

      if (isFunction(cmpCallback)) {
        // call the mapped callback and then remove the reference
        cmpCallback(payload.returnValue, payload.success);
        cmpCallbacks.delete(payload.callId);
      }
    }
  }

  if (cmpFrame) {
    win.addEventListener('message', postMessageProxyHandler, false);
    win.__tcfapi = tcfapiProxyHandler;
  }
}
